import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Eye, Trash2, GripVertical } from 'lucide-react';

interface HelpCenterCardProps {
  id: number;
  title: string;
  description?: string;
  content: string;
  display_order: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  mode: 'admin' | 'client';
  onEdit?: (card: HelpCenterCardData) => void;
  onDelete?: (id: number) => void;
  onView?: (card: HelpCenterCardData) => void;
  dragHandleProps?: any;
}

export interface HelpCenterCardData {
  id: number;
  title: string;
  description?: string;
  content: string;
  display_order: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

function HelpCenterCard({
  id,
  title,
  description,
  content,
  display_order,
  is_published,
  created_at,
  updated_at,
  mode,
  onEdit,
  onDelete,
  onView,
  dragHandleProps
}: HelpCenterCardProps) {
  // Extract plain text from HTML content for preview

  const cardData: HelpCenterCardData = {
    id,
    title,
    description,
    content,
    display_order,
    is_published,
    created_at,
    updated_at
  };

  if (mode === 'client') {
    return (
      <Card
        className="cursor-pointer hover:shadow-md transition-shadow duration-200"
        onClick={() => onView?.(cardData)}
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-lg line-clamp-2">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground line-clamp-3">
            {description}
          </p>
          <div className="mt-3 flex items-center justify-end">
            <span className="text-xs text-muted-foreground">
              Updated {new Date(updated_at).toLocaleDateString()}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Admin mode
  return (
    <Card className="group hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg line-clamp-2 pr-2">{title}</CardTitle>
              {!is_published && (
                <Badge variant="secondary" className="text-xs">
                  Draft
                </Badge>
              )}
            </div>
            {description && (
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                {description}
              </p>
            )}
          </div>
          {dragHandleProps && (
            <div
              {...dragHandleProps}
              className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            Updated {new Date(updated_at).toLocaleDateString()}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onView?.(cardData)}
              className="h-8 px-2"
            >
              <Eye className="h-3 w-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit?.(cardData)}
              className="h-8 px-2"
            >
              <Edit className="h-3 w-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete?.(id)}
              className="h-8 px-2 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default HelpCenterCard;